import '@babel/polyfill';
import React, { Component } from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { IntlProvider, injectIntl } from 'react-intl';
import Head from 'next/head';
import withRedux from 'next-redux-wrapper';
import { parse } from 'url';
import '../clientInitialization';
import {
  locationChange,
  disableTrackingImpressions,
  enableTrackingImpressions,
} from '../tracking/actions';
import makeStore from '../store';
import {
  initializeTrovit,
  initializeConfig,
  updateTrovit,
  hideDownloadAppBanner,
} from '../Index/actions';
import { initializeTypeId } from '../Serp/actions';
import { initializePersonalizedAds, initializeGdprCookieBanner } from '../Gdpr/actions';
import { fetchSourcesRequest } from '../Sources/list/actions';
import Router from '../router';
import clientConfig from '../../parameters/client/config';
import TrackingApi from '../sdk/tracking/Api';
import { buildMetricsData } from '../sdk/common/utils';
import { pageView, adImpressions } from '../tracking/eventDefinitions';
import { Error } from '../Error/components';
import { isQueryAlertLanding } from '../request/query/utils';
import internalstatsOpenAlertApiTracking from '../tracking/events/internalstatsOpenAlert/apiTracking';
import internalstatsInternalClicksApiTracking from '../tracking/events/internalstatsInternalClicks/apiTracking';
import {
  COOKIE_TROVIT_SHOW_DOWNLOAD_APP_BANNER,
  COOKIE_TROVIT_SHOW_PERMISSIONS_BANNER_COUNTER,
} from '../cookies';
import { getVerticalName } from '~/common/selectors/config';
import { showingFiltersSelector, isFetchingSelector } from '~/Serp/selectors';

const trackingApi = new TrackingApi(clientConfig.trackingApi);

let isRouterInitialized = false;
// Flag that equals true when the user enters to the app for the first time
// through url
let firstAppAccess = true;
let commingFromBackButton = false;

const computeAbsoluteUrl = (req, isServer, asPath) => {
  if (isServer) {
    const currentPath =
      req.originalUrl.split('?')[0] === '/q' ? req.originalUrl : req.originalUrl.split('?')[0];
    return decodeURI(`${req.protocol}://${req.get('host')}${currentPath}`);
  }

  const url = parse(asPath);

  return decodeURI(
    `${window.location.protocol}//${window.location.hostname}${
      window.location.port ? `:${window.location.port}` : ''
    }${url.path}`
  );
};

export default Page => {
  const IntlPage = injectIntl(Page);

  class PageWithReduxAndIntl extends Component {
    static async getInitialProps(context) {
      const { req, isServer, res, store, asPath } = context;
      const url = computeAbsoluteUrl(req, isServer, asPath);

      // Get the `locale` and `messages` from the request object on the server.
      // In the browser, use the same values that the server serialized.
      // NOTE: Because the use of `withRedux` initial properties and state are
      // stored within `__NEXT_DATA__.props`.
      const { config } = req || window.__NEXT_DATA__.props.pageProps.initialProps; // eslint-disable-line no-underscore-dangle
      let query = context.query;
      let props = {};

      // Tasks to do on server side
      if (isServer) {
        // Check if route query has DSA params and attach to the nextjs app query.
        if (req.query.sem || query.sem) {
          query = { ...query, ...req.query, ...req.query.dsa };
          context.query = query; // eslint-disable-line no-param-reassign
        }

        try {
          const queryAlertLanding = isQueryAlertLanding(Number(req.query.alertLanding));

          // Dispatch to store config in the state
          await store.dispatch(initializeConfig(req.config));

          const { isGdprCookieBannerVisible, personalizedAds } = req.config.gdpr;

          await store.dispatch(initializePersonalizedAds(personalizedAds));
          await store.dispatch(
            initializeGdprCookieBanner(
              isGdprCookieBannerVisible,
              req.config.vertical.cookie_disclaimer_url
            )
          );

          if (queryAlertLanding) {
            res.cookie(COOKIE_TROVIT_SHOW_DOWNLOAD_APP_BANNER, false);
            res.cookie(COOKIE_TROVIT_SHOW_PERMISSIONS_BANNER_COUNTER, 100);
            await store.dispatch(hideDownloadAppBanner());
          }
        } catch (error) {
          throw new Error('Request config is missing');
        }

        // related to https://jira.services/browse/PWA-1653
        if (req.config.countryCode === 'fr' || req.config.countryCode === 'in') {
          req.abTest = {
            id: 666,
            option: 'B',
            platform: 'mobile',
          };
        }

        await store.dispatch(
          initializeTrovit({
            installationId: req.installationId,
            origin: req.origin,
            trackingCodes: req.trackingCodes,
            sendingId: req.sendingId,
            userCampaignIds: req.userCampaignIds,
            query,
            url,
            userAgent: req.headers['user-agent'],
            userIp: req.clientIp,
            device: req.device,
            abTest: req.abTest || {},
            publishYourCv: req.publishYourCv,
            imagePlaceHolders: req.imagePlaceHolders,
            host: req.headers.host,
            bot: Boolean(req.bot),
          })
        );

        const verticalName = getVerticalName(store.getState());
        await store.dispatch(initializeTypeId(verticalName));

        // Initialize router at this point so it was valid when rendering on server side.
        Router.initialize(config.routes, req.device);
      }

      // Initialize page running sagas
      if (isServer && typeof Page.runInitialSagas === 'function') {
        // Dispatch for fetching initial data
        await Page.runInitialSagas(context);
        await store.dispatch(fetchSourcesRequest());
        await store.breakAndWaitSagas();
      }

      if (typeof Page.getInitialProps === 'function') {
        props = await Page.getInitialProps(context);
      }

      // Only break sagas in server side, we need them to still be running in the client
      if (isServer) {
        await store.breakAndWaitSagas();
      }

      // Update response statusCode with the one returned by the component's getInitialProps method.
      if (props && props.statusCode && res) {
        res.statusCode = props.statusCode;
      }

      if (isServer) {
        // Dispatch to update the trovit data in the state
        await store.dispatch(
          updateTrovit({
            statusCode: props && props.statusCode,
          })
        );

        // The first pageView and pageImpression are tracked internally from the server
        const storeState = store.getState();
        const configState = storeState.config;
        const countryCode = configState.countryCode;
        const verticalName = configState.verticalName;

        const firstPageviewEvent = pageView(null, null, storeState);
        const pageViewsMetricsData = buildMetricsData(
          verticalName,
          countryCode,
          'tracking',
          'pageView'
        );

        trackingApi.pageview(firstPageviewEvent.eventMeta, pageViewsMetricsData);

        const firstPageviewAdImpressionsEvents = adImpressions(null, null, store.getState());
        if (firstPageviewAdImpressionsEvents) {
          const adImpressionsMetricsData = buildMetricsData(
            verticalName,
            countryCode,
            'tracking',
            'adImpression'
          );
          firstPageviewAdImpressionsEvents.forEach(event =>
            trackingApi.adImpressions(event.eventMeta, adImpressionsMetricsData)
          );
        }
      }

      // Always update the current time on page load/transition because the
      // <IntlProvider> will be a new instance even with pushState routing.
      const now = Date.now();

      return {
        ...props,
        isServer,
        query,
        config,
        now,
        url,
      };
    }

    /**
     * Action in this constructor are necessary because when a page is rendered
     * in server side the getInitialProps() method is not executed again in the
     * client side.
     */
    constructor(props) {
      super(props);
      // Initialize router at this point so it was valid when rendering on client side.
      if (typeof window !== 'undefined' && !isRouterInitialized) {
        Router.initialize(
          window.__NEXT_DATA__.props.pageProps.initialProps.config.routes, // eslint-disable-line no-underscore-dangle
          window.__NEXT_DATA__.props.pageProps.initialState.trovit.device // eslint-disable-line no-underscore-dangle
        );
        isRouterInitialized = true;
      }
    }

    componentWillMount() {
      // Handle when user press back button on browser
      // to avoid send duplicate tracking impressions
      // - This code should be removed once clickout is stabilized
      if (typeof window !== 'undefined' && window.onpopstate !== 'function') {
        window.onpopstate = () => {
          this.props.disableTrackingImpressions();
          commingFromBackButton = true;
        };
      }
      if (!commingFromBackButton) {
        this.props.enableTrackingImpressions();
      }
      commingFromBackButton = false;
      // - end code to remove
    }

    componentDidMount() {
      // Global variable for Google Tag Manager
      if (!window.trovitTracking) {
        window.trovitTracking = trackingApi;
      }

      const { statusCode } = this.props;

      if (!firstAppAccess) {
        // Emit a location change only in page changes starting from the second app access
        const { url, query } = this.props;
        this.props.locationChange({ url, query, statusCode });
      } else if (!statusCode) {
        this.trackPage();
      }

      firstAppAccess = false;
    }

    componentWillUpdate(nextProps) {
      // - This code should be removed once clickout is stabilized
      const isUpdatingTwice = nextProps.url === this.props.url;

      if (!commingFromBackButton && !isUpdatingTwice) {
        this.props.enableTrackingImpressions();
      }

      if (!isUpdatingTwice) {
        commingFromBackButton = false;
      }
      // - end code to remove
    }

    trackPage() {
      const {
        sendInternalstatsOpenAlertApiTracking,
        sendInternalstatsInternalClicksApiTracking,
        query,
      } = this.props;

      if (query.from === 'alert') {
        sendInternalstatsOpenAlertApiTracking();
      }
      if (typeof query.internalsection === 'string') {
        sendInternalstatsInternalClicksApiTracking();
      }
    }

    render() {
      const { config, now, statusCode, url, query, showingFilters, isFetching } = this.props;
      const routeName = query ? query.routeName : null;

      return (
        <IntlProvider
          locale={get(config, 'locale')}
          messages={get(config, 'messages')}
          initialNow={now}
          formats={{
            relative: {
              days: {
                units: 'day',
                style: 'numeric',
              },
              default: {
                style: 'best fit',
              },
            },
          }}
        >
          {/*
            Now, Filters is a component rendered in the Serp and most of the logic is in the Serp component.
            When there is an error, Error component is rendered and Serp component is unmounted so the Filters
            component disappears and when we click in the Filters link, Filters is not shown.
            We should generate a Filters component outside of the Serp, meanwhile we have added this showingFilters
            and isFetching condition to not unmount the Serp when there is an error
          */}
          {statusCode && !showingFilters && !isFetching ? (
            <Error statusCode={statusCode} routeName={routeName} />
          ) : (
            <div>
              <Head>
                <meta property="og:url" content={url} />
                <link rel="canonical" href={url} />
              </Head>
              <IntlPage {...this.props} />
            </div>
          )}
        </IntlProvider>
      );
    }
  }

  PageWithReduxAndIntl.propTypes = {
    config: PropTypes.object.isRequired,
    query: PropTypes.object,
    locationChange: PropTypes.func.isRequired,
    enableTrackingImpressions: PropTypes.func.isRequired,
    disableTrackingImpressions: PropTypes.func.isRequired,
    url: PropTypes.string.isRequired,
    statusCode: PropTypes.number,
    now: PropTypes.number,
    sendInternalstatsOpenAlertApiTracking: PropTypes.func.isRequired,
    sendInternalstatsInternalClicksApiTracking: PropTypes.func.isRequired,
    showingFilters: PropTypes.bool.isRequired,
    isFetching: PropTypes.bool.isRequired,
  };

  PageWithReduxAndIntl.defaultProps = {
    now: Date.now(),
    statusCode: null,
    query: null,
  };

  const mapStateToProps = state => ({
    sendInternalstatsOpenAlertApiTracking: () => internalstatsOpenAlertApiTracking(state).send(),
    sendInternalstatsInternalClicksApiTracking: () =>
      internalstatsInternalClicksApiTracking(state).send(),
    showingFilters: showingFiltersSelector(state),
    isFetching: isFetchingSelector(state),
  });

  const mapDispatchToProps = dispatch => ({
    locationChange: data => dispatch(locationChange(data)),
    enableTrackingImpressions: () => dispatch(enableTrackingImpressions()),
    disableTrackingImpressions: () => dispatch(disableTrackingImpressions()),
  });

  return withRedux(makeStore, mapStateToProps, mapDispatchToProps)(PageWithReduxAndIntl);
};



// WEBPACK FOOTER //
// ./app/hoc/PageWithReduxAndIntl.js